import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { apiRequest } from '../lib/api-client';
import { messageFor } from '../lib/errors';

/**
 * "Reportar um problema ou sugerir melhoria" (Phase 129).
 *
 * This took the place of the dependency check that used to sit here — "postgres ✓ redis ✓
 * rabbitmq ✓" is an operator's answer to an operator's question, and the person running a bakery
 * does not have that question. What they have, constantly, is the other one: this is wrong, or
 * this could be better. Until now the product had no way to hear it, and a product with no way
 * back hears only from people annoyed enough to go and find an e-mail address — by which point
 * they have usually decided something about it.
 *
 * **The current path is sent with the report**, which is the difference between a message somebody
 * can act on and one they cannot: "não consigo salvar" is unactionable, and the same words from
 * `/crm/oportunidades/:id` are a bug report.
 */
export function ReportPanel(): React.JSX.Element {
  const location = useLocation();
  const [kind, setKind] = useState<'PROBLEM' | 'IDEA'>('PROBLEM');
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = useMutation({
    mutationFn: () =>
      apiRequest('/v1/feedback', {
        method: 'POST',
        body: { kind, message: message.trim(), page: location.pathname + location.search },
      }),
    onSuccess: () => {
      setMessage('');
      setError(null);
      setSent(true);
    },
    onError: (caught: unknown) => {
      setSent(false);
      setError(messageFor(caught));
    },
  });

  if (sent) {
    return (
      <div className="report report--done">
        <p className="strong">{t('report.thanks')}</p>
        <p className="muted small">{t('report.thanksBody')}</p>

        <button
          type="button"
          className="button button--quiet button--compact"
          onClick={() => setSent(false)}
        >
          {t('report.another')}
        </button>
      </div>
    );
  }

  return (
    <form
      className="report"
      onSubmit={(event) => {
        event.preventDefault();
        if (message.trim().length >= 5) send.mutate();
      }}
    >
      <p className="report__title">{t('report.title')}</p>

      {/* Two words, because the two go to different places in somebody's head. */}
      <div className="report__kinds">
        {(['PROBLEM', 'IDEA'] as const).map((option) => (
          <button
            key={option}
            type="button"
            className={`chip${kind === option ? ' chip--active' : ''}`}
            aria-pressed={kind === option}
            onClick={() => setKind(option)}
          >
            {t(`report.kind.${option}`)}
          </button>
        ))}
      </div>

      <textarea
        className="input report__text"
        rows={3}
        maxLength={4000}
        value={message}
        placeholder={t(kind === 'PROBLEM' ? 'report.problemHint' : 'report.ideaHint')}
        onChange={(event) => setMessage(event.target.value)}
      />

      {error && (
        <p className="alert alert--compact" role="alert">
          {error}
        </p>
      )}

      <div className="report__foot">
        {/* Said out loud rather than sent quietly: it is their screen, and they should know. */}
        <span className="muted small">
          {t('report.sendsPage').replace('{page}', location.pathname)}
        </span>

        <button
          type="submit"
          className="button button--primary button--compact"
          disabled={message.trim().length < 5 || send.isPending}
        >
          {send.isPending ? t('common.saving') : t('report.send')}
        </button>
      </div>
    </form>
  );
}
